"use client";

import React from "react";
import { Search, MapPin, User, Funnel as Filter, ChevronRight, Shield } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProfileGridConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border shadow-sm">
            <Skeleton className="aspect-[3/4] w-full" />
            <CardHeader className="p-4">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="mt-2 h-4 w-2/3" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border p-12 text-center">
        <Shield className="mb-4 size-12 text-muted-foreground" />
        <h3 className="mb-2 text-lg font-semibold text-foreground">Gagal Memuat Data</h3>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border p-12 text-center">
        <User className="mb-4 size-12 text-muted-foreground" />
        <h3 className="mb-2 text-lg font-semibold text-foreground">Tidak Ada Profil Ditemukan</h3>
        <p className="text-sm text-muted-foreground">Coba sesuaikan filter pencarian Anda.</p>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const imageSrc = post.customFields?.gambar || post.featuredImage;
        const image = imageSrc ? {
          src: imageSrc,
          srcset: "",
          sizes: ""
        } : null;
        const nama = post.customFields?.nama || post.title;
        const peran = post.customFields?.peran || "Anggota";
        const wilayah = post.customFields?.wilayah || "Pusat";
        const status = post.customFields?.status || "Aktif";
        const deskripsi = post.customFields?.deskripsi || post.excerpt?.replace(/<[^>]*>?/gm, '') || "";
        return <Card key={post.id} data-index={i} className="group flex flex-col overflow-hidden border-border shadow-sm transition-all hover:shadow-md">
              <Link to={post.link || "#"} className="relative block aspect-[4/5] overflow-hidden bg-muted">
                {image ? <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={nama} loading="lazy" className="size-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex size-full items-center justify-center bg-secondary/10">
                    <User className="size-20 text-secondary/40" />
                  </div>}
                <div className="absolute top-3 right-3 flex flex-col gap-2">
                  <Badge variant={status.toLowerCase() === 'aktif' ? 'default' : 'secondary'} className="shadow-sm">
                    {status}
                  </Badge>
                </div>
              </Link>
              
              <CardHeader className="p-5 pb-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-primary">
                  <Shield className="size-3.5" />
                  <span>{peran}</span>
                </div>
                <h3 className="font-default text-lg font-bold leading-tight text-foreground line-clamp-2">
                  <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                    {nama}
                  </Link>
                </h3>
              </CardHeader>
              
              <CardContent className="flex-1 p-5 pt-0">
                <div className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="size-4 shrink-0" />
                  <span className="truncate">{wilayah}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {deskripsi}
                </p>
              </CardContent>
              
              <CardFooter className="border-t border-border p-5">
                <Button asChild variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
                  <Link to={post.link || "#"}>
                    Lihat Profil Lengkap
                    <ChevronRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {}
      {(hasNext || hasPrev) && <div className="flex items-center justify-center gap-4 pt-4 border-t border-border">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="w-32">
            Sebelumnya
          </Button>
          <Button variant="default" onClick={nextPage} disabled={!hasNext} className="w-32">
            Selanjutnya
          </Button>
        </div>}
    </div>;
}
export default function ZonaPPProfileDirectory() {
  const wp_query_params = {
    post_type: "zona_pp_profile",
    per_page: 9,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  const wilayahOptions = ["Pusat", "Jawa", "Sumatera", "Kalimantan", "Sulawesi", "Papua", "Bali-NTT"];
  const statusOptions = ["Aktif", "Arsip"];
  return <section id="zonappprofiledirectory" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="mb-10 md:mb-16 max-w-3xl">
          <div className="mb-4 flex items-center gap-2">
            <span className="h-px w-8 bg-primary"></span>
            <span className="text-sm font-bold uppercase tracking-wider text-primary">Direktori Organisasi</span>
          </div>
          <h2 className="font-default mb-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            Tokoh & Senior <span className="text-primary">Pemuda Pancasila</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Mengenal para tokoh, pendiri, ketua, senior dan kader Pemuda Pancasila tingkat nasional hingga wilayah berdasarkan data resmi dan terverifikasi.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4 lg:gap-12">
          
          {}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-8 rounded-xl border border-border bg-card p-6 shadow-sm">
              
              <div>
                <h3 className="font-default mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
                  <Search className="size-5 text-primary" />
                  Pencarian Tokoh
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input type="text" placeholder="Masukkan nama tokoh..." className="pl-9 bg-background" />
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="font-default mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
                  <MapPin className="size-5 text-primary" />
                  Wilayah
                </h3>
                <div className="space-y-3">
                  {wilayahOptions.map((option, i) => <div key={option} data-index={i} className="flex items-center space-x-3">
                      <Checkbox id={`wilayah-${i}`} />
                      <Label htmlFor={`wilayah-${i}`} className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {option}
                      </Label>
                    </div>)}
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="font-default mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
                  <Filter className="size-5 text-primary" />
                  Status
                </h3>
                <div className="space-y-3">
                  {statusOptions.map((option, i) => <div key={option} data-index={i} className="flex items-center space-x-3">
                      <Checkbox id={`status-${i}`} defaultChecked={option === "Aktif"} />
                      <Label htmlFor={`status-${i}`} className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                        {option}
                      </Label>
                    </div>)}
                </div>
              </div>

              <Button className="w-full mt-4">
                Terapkan Filter
              </Button>

            </div>
          </aside>

          {}
          <div className="lg:col-span-3">
            <div className="mb-6 flex items-center justify-between border-b border-border pb-4">
              <h3 className="font-default text-xl font-semibold text-foreground">
                Hasil Direktori
              </h3>
              <span className="text-sm text-muted-foreground">
                Menampilkan data terbaru
              </span>
            </div>
            
            <PostsProvider wp_query={{
            post_type: "zona_pp_profile",
            per_page: 9,
            orderby: "date",
            order: "desc"
          }}>
              <ProfileGridConsumer />
            </PostsProvider>
          </div>

        </div>
      </div>
    </section>;
}